import type {
  PlantaInversor,
  PlantaUG,
  PlantaUGAtMinutes,
} from "../components/SunBurstChart";
import { getNumberOfUgName } from "../../usinas/ugsMapper";
import type { UG } from "../../usinas/types";

const ZERO_DISPLAY_EPSILON = 0.0001;
const MIN_ZERO_SIBLING_SHARE_OF_MAX = 0.12;
const MIN_ZERO_SIBLING_SHARE_OF_AVG_POSITIVE = 0.25;

export function toSunburstDisplayValues(
  actualValues: (number | undefined)[],
  minShareOfMax = MIN_ZERO_SIBLING_SHARE_OF_MAX,
): number[] {
  const actuals = actualValues.map((value) => value ?? 0);
  const positiveValues = actuals.filter((value) => value > 0);
  const positiveSum = positiveValues.reduce((sum, value) => sum + value, 0);
  const maxActual = Math.max(...actuals, 0);

  if (maxActual === 0) {
    return actuals.map(() => 1);
  }

  const avgPositive = positiveSum / positiveValues.length;
  const minFromAverage = avgPositive * MIN_ZERO_SIBLING_SHARE_OF_AVG_POSITIVE;
  const minFromMax = maxActual * minShareOfMax;
  const minDisplay = Math.max(minFromAverage, minFromMax);

  return actuals.map((actual) => (actual > 0 ? actual : minDisplay));
}

export type SunburstChartNodeInput = {
  name?: string;
  actualValue?: number;
  children?: SunburstChartNodeInput[];
};

export type SunburstChartNode = {
  name?: string;
  value: number;
  actualValue: number;
  children?: SunburstChartNode[];
};

export function buildSunburstChartNodes(
  nodes: SunburstChartNodeInput[],
): SunburstChartNode[] {
  const builtNodes = nodes.map((node) => {
    const actualValue = node.actualValue ?? 0;
    const children = node.children?.length
      ? buildSunburstChartNodes(node.children)
      : undefined;

    return {
      name: node.name,
      actualValue,
      children,
    };
  });

  const displayValues = toSunburstDisplayValues(
    builtNodes.map((node) => node.actualValue),
  );

  return builtNodes.map((node, index) => {
    const childrenSum =
      node.children?.reduce((sum, child) => sum + child.value, 0) ?? 0;
    const siblingDisplayValue = displayValues[index];
    const value =
      node.children?.length && node.actualValue > 0
        ? Math.max(childrenSum, siblingDisplayValue)
        : siblingDisplayValue;

    return {
      ...node,
      value,
    };
  });
}

export function toSunburstDisplayValue(
  value?: number,
  minVisible = ZERO_DISPLAY_EPSILON,
): number {
  if (value == null || value === 0) return minVisible;
  return value;
}

function getUgDisplayName(name?: string, fallbackNumber?: number): string {
  const ugNumber = getNumberOfUgName(name) ?? fallbackNumber;
  return ugNumber !== undefined ? `UG ${ugNumber}` : "UG";
}

function isGenericUgName(name?: string): boolean {
  if (!name?.trim()) return true;
  return getNumberOfUgName(name) === undefined;
}

function findApiUgByNumber<T extends { name?: string }>(
  apiUGs: T[],
  ugNumber: number,
  usedApiIndices: Set<number>,
): T | undefined {
  const apiIndex = apiUGs.findIndex(
    (ug, index) =>
      !usedApiIndices.has(index) &&
      getNumberOfUgName(ug.name) === ugNumber,
  );

  if (apiIndex < 0) return undefined;

  usedApiIndices.add(apiIndex);
  return apiUGs[apiIndex];
}

function buildConfiguredApiMatches<T extends { name?: string }>(
  sortedConfigured: UG[],
  apiUGs: T[] | undefined,
): { matches: Array<T | undefined>; usedApiIndices: Set<number> } {
  const apiList = [...(apiUGs ?? [])];
  const matches: Array<T | undefined> = new Array(sortedConfigured.length).fill(
    undefined,
  );
  const usedApiIndices = new Set<number>();

  sortedConfigured.forEach((configUg, configIndex) => {
    const ugNumber = getNumberOfUgName(configUg.name);
    if (ugNumber === undefined) return;

    const apiUg = findApiUgByNumber(apiList, ugNumber, usedApiIndices);
    if (apiUg) {
      matches[configIndex] = apiUg;
    }
  });

  const unmatchedConfigIndices = sortedConfigured
    .map((_, index) => index)
    .filter((index) => matches[index] === undefined);

  apiList.forEach((apiUg, apiIndex) => {
    if (usedApiIndices.has(apiIndex) || !isGenericUgName(apiUg.name)) return;

    const configIndex = unmatchedConfigIndices.shift();
    if (configIndex === undefined) return;

    matches[configIndex] = apiUg;
    usedApiIndices.add(apiIndex);
  });

  return { matches, usedApiIndices };
}

function getExtraApiUgs<T extends { name?: string }>(
  apiUGs: T[] | undefined,
  sortedConfigured: UG[],
  usedApiIndices: Set<number>,
): T[] {
  const configuredNumbers = getConfiguredUgNumbers(sortedConfigured);

  return (apiUGs ?? []).filter((apiUg, index) => {
    if (usedApiIndices.has(index)) return false;

    const num = getNumberOfUgName(apiUg.name);
    if (num === undefined) return false;

    return !configuredNumbers.has(num);
  });
}

function buildInversoresFromConfig(configUg: UG): PlantaInversor[] {
  const firstInverterNumber =
    configUg.components.inverters?.firstInverterNumber ?? 1;
  const amount = configUg.components.inverters?.amount ?? 0;
  if (amount <= 0) return [];

  const maxInverterNumber = firstInverterNumber + amount - 1;
  const inversores: PlantaInversor[] = [];

  for (let i = firstInverterNumber; i <= maxInverterNumber; i++) {
    inversores.push({
      name: `INV ${i}`,
      energiaGerada: { value: 0 },
      potenciaAtiva: { value: 0 },
    });
  }

  return inversores;
}

function mergeInversores(
  apiInversores: PlantaInversor[] | undefined,
  configUg: UG,
): PlantaInversor[] {
  if (apiInversores?.length) return apiInversores;
  return buildInversoresFromConfig(configUg);
}

function sortConfiguredUGs(configuredUGs: UG[]): UG[] {
  return [...configuredUGs].sort((a, b) => {
    const aNum = getNumberOfUgName(a.name) ?? Number.MAX_SAFE_INTEGER;
    const bNum = getNumberOfUgName(b.name) ?? Number.MAX_SAFE_INTEGER;
    return aNum - bNum;
  });
}

function getConfiguredUgNumbers(configuredUGs: UG[]): Set<number> {
  return new Set(
    configuredUGs
      .map((ug) => getNumberOfUgName(ug.name))
      .filter((n): n is number => n !== undefined),
  );
}

export function mergePlantaUGsWithUsinaConfig(
  configuredUGs: UG[] | undefined,
  apiUGs: PlantaUG[] | undefined,
): PlantaUG[] {
  if (!configuredUGs?.length) return apiUGs ?? [];

  const sortedConfigured = sortConfiguredUGs(configuredUGs);
  const { matches, usedApiIndices } = buildConfiguredApiMatches(
    sortedConfigured,
    apiUGs,
  );

  const merged: PlantaUG[] = sortedConfigured.map((configUg, index) => {
    const ugNumber = getNumberOfUgName(configUg.name);
    const apiUg = matches[index];

    if (apiUg) {
      return {
        ...apiUg,
        name: getUgDisplayName(configUg.name, ugNumber),
        inversores: mergeInversores(apiUg.inversores, configUg),
      };
    }

    return {
      name: getUgDisplayName(configUg.name, ugNumber),
      energiaGerada: { value: 0 },
      potenciaAtiva: { value: 0 },
      inversores: buildInversoresFromConfig(configUg),
    };
  });

  const extraApiUgs = getExtraApiUgs(apiUGs, sortedConfigured, usedApiIndices);

  return [...merged, ...extraApiUgs];
}

export function mergePlantaUGsAtMinutesWithUsinaConfig(
  configuredUGs: UG[] | undefined,
  apiUGs: PlantaUGAtMinutes[] | undefined,
): PlantaUGAtMinutes[] {
  if (!configuredUGs?.length) return apiUGs ?? [];

  const sortedConfigured = sortConfiguredUGs(configuredUGs);
  const { matches, usedApiIndices } = buildConfiguredApiMatches(
    sortedConfigured,
    apiUGs,
  );

  const merged: PlantaUGAtMinutes[] = sortedConfigured.map((configUg, index) => {
    const ugNumber = getNumberOfUgName(configUg.name);
    const apiUg = matches[index];

    if (apiUg) {
      return {
        ...apiUg,
        name: getUgDisplayName(configUg.name, ugNumber),
        inversores: apiUg.inversores ?? [],
      };
    }

    return {
      name: getUgDisplayName(configUg.name, ugNumber),
      energiaGerada: { value: 0 },
      potenciaAtiva: { value: 0 },
      lastPotenciaAtivaMinute: 0,
      potenciaAtivaAtMinutes: [],
      inversores: [],
    };
  });

  const extraApiUgs = getExtraApiUgs(apiUGs, sortedConfigured, usedApiIndices);

  return [...merged, ...extraApiUgs];
}
